import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import api from "../../services/api";

function getCurrentIsoWeekYear() {
  const now = new Date();
  const date = new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate()));
  const dayNum = date.getUTCDay() || 7;
  date.setUTCDate(date.getUTCDate() + 4 - dayNum);
  const year = date.getUTCFullYear();
  const yearStart = new Date(Date.UTC(year, 0, 1));
  const week = Math.ceil((((date - yearStart) / 86400000) + 1) / 7);
  return { weekNumber: week, year };
}

function getIsoWeekRange(year, weekNumber) {
  const jan4 = new Date(Date.UTC(year, 0, 4));
  const jan4Day = jan4.getUTCDay() || 7;
  const week1Monday = new Date(jan4);
  week1Monday.setUTCDate(jan4.getUTCDate() - jan4Day + 1);

  const start = new Date(week1Monday);
  start.setUTCDate(week1Monday.getUTCDate() + (weekNumber - 1) * 7);
  start.setUTCHours(0, 0, 0, 0);

  const endExclusive = new Date(start);
  endExclusive.setUTCDate(start.getUTCDate() + 7);
  return { start, endExclusive };
}

function toDate(value) {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date;
}

function inRange(date, start, endExclusive) {
  const value = toDate(date);
  if (!value) return false;
  return value >= start && value < endExclusive;
}

function hasHoursInWeek(werkbon, start, endExclusive) {
  for (const entry of werkbon?.hourEntries || []) {
    if (inRange(entry?.datum, start, endExclusive) && Number(entry?.hours || 0) > 0) {
      return true;
    }
  }
  for (const entry of werkbon?.autoAdjustments || []) {
    if (inRange(entry?.datum, start, endExclusive) && Number(entry?.hours || 0) > 0) {
      return true;
    }
  }
  for (const entry of werkbon?.autoTimeBaseEntries || []) {
    if (
      inRange(entry?.datum || entry?.startAt || entry?.endAt, start, endExclusive) &&
      Number(entry?.hours || 0) > 0
    ) {
      return true;
    }
  }
  return false;
}

function formatDate(value) {
  const date = toDate(value);
  if (!date) return "-";
  return date.toLocaleDateString("nl-NL");
}

function formatDateTime(value) {
  const date = toDate(value);
  if (!date) return "-";
  return date.toLocaleString("nl-NL", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatHours(value) {
  const num = Number(value || 0);
  if (!Number.isFinite(num)) return "0.00";
  return num.toFixed(2);
}

function formatScopeType(value) {
  const key = String(value || "").trim().toLowerCase();
  if (key === "project") return "Project";
  if (key === "workorder") return "Werkbon";
  return "Klant";
}

function formatStatusLabel(value) {
  const key = String(value || "").trim().toLowerCase();
  if (key === "draft") return "Concept";
  if (key === "generated") return "Generated";
  if (key === "signed") return "Ondertekend";
  if (key === "invoiced") return "Gefactureerd";
  return value || "-";
}

function getSigningState(row) {
  const publicStatus = String(row?.publicSignatureStatus || "").trim().toLowerCase();
  if (publicStatus === "rejected") return "rejected";
  if (publicStatus === "signed") return "signed";
  if (row?.publicSignatureRequestedAt) return "pending";
  if (String(row?.status || "").trim().toLowerCase() === "signed") return "manual_signed";
  return "not_sent";
}

function formatSigningStateLabel(state) {
  if (state === "pending") return "Openstaand";
  if (state === "signed") return "Publiek ondertekend";
  if (state === "rejected") return "Afgewezen";
  if (state === "manual_signed") return "Handmatig ondertekend";
  return "Niet verzonden";
}

function formatSigningActivity(row) {
  if (row?.publicSignatureRespondedAt) {
    return `Beantwoord ${formatDateTime(row.publicSignatureRespondedAt)}`;
  }
  if (row?.publicSignatureRequestedAt) {
    return `Verzonden ${formatDateTime(row.publicSignatureRequestedAt)}`;
  }
  if (row?.signedAt) {
    return `Ondertekend ${formatDateTime(row.signedAt)}`;
  }
  return "-";
}

function deriveProjectsFromWerkbonnen(werkbonnen) {
  const byId = new Map();
  for (const werkbon of werkbonnen || []) {
    const rawProject = werkbon?.project;
    const rawProjectId = werkbon?.projectId || rawProject?._id;
    const projectId =
      typeof rawProjectId === "object" && rawProjectId?._id
        ? String(rawProjectId._id)
        : String(rawProjectId || "").trim();
    if (!projectId || byId.has(projectId)) continue;
    byId.set(projectId, {
      _id: projectId,
      naam:
        rawProject?.naam ||
        rawProject?.title ||
        rawProject?.projectNummer ||
        rawProject?.number ||
        werkbon?.projectNaam ||
        werkbon?.projectNumber ||
        werkbon?.projectNummer ||
        "",
    });
  }
  return [...byId.values()];
}

function getKlantId(row) {
  return String(row?.klantId?._id || row?.klantId || "").trim();
}

function getProjectId(row) {
  return String(row?.scopeType || "").trim().toLowerCase() === "project"
    ? String(row?.scopeId || "").trim()
    : "";
}

function getWorkorderId(row) {
  return String(row?.scopeType || "").trim().toLowerCase() === "workorder"
    ? String(row?.scopeId || "").trim()
    : "";
}

function getWorkorderLabel(werkbon) {
  return (
    werkbon?.werkbonNummer ||
    werkbon?.nummer ||
    werkbon?.omschrijving ||
    werkbon?._id ||
    ""
  );
}

function getProjectLabel(project) {
  return (
    project?.naam ||
    project?.title ||
    project?.projectNummer ||
    project?.number ||
    project?._id ||
    ""
  );
}

function getScopeDisplayName(row, { projectsById, werkbonnenById }) {
  const scopeType = String(row?.scopeType || "").trim().toLowerCase();
  const scopeId = String(row?.scopeId || "").trim();
  if (scopeType === "project") {
    const project = projectsById.get(scopeId);
    return getProjectLabel(project) || row?.scopeLabelSnapshot || "-";
  }
  if (scopeType === "workorder") {
    const werkbon = werkbonnenById.get(scopeId);
    return getWorkorderLabel(werkbon) || row?.scopeLabelSnapshot || "-";
  }
  return row?.scopeLabelSnapshot || row?.klantNaamSnapshot || "-";
}

function getBadgeClass(kind) {
  if (kind === "signed") return "border-emerald-200 bg-emerald-50 text-emerald-700";
  if (kind === "pending") return "border-amber-200 bg-amber-50 text-amber-700";
  if (kind === "rejected") return "border-rose-200 bg-rose-50 text-rose-700";
  if (kind === "invoiced") return "border-sky-200 bg-sky-50 text-sky-700";
  return "border-slate-200 bg-slate-50 text-slate-700";
}

function StatCard({ label, value, tone = "default" }) {
  return (
    <div className={`rounded-2xl border px-4 py-3 ${getBadgeClass(tone)}`}>
      <p className="text-xs font-semibold uppercase tracking-wide">{label}</p>
      <p className="mt-1 text-2xl font-bold">{value}</p>
    </div>
  );
}

function InfoBadge({ label, tone = "default" }) {
  return (
    <span className={`inline-flex rounded-full border px-2.5 py-1 text-xs font-medium ${getBadgeClass(tone)}`}>
      {label}
    </span>
  );
}

export default function MandagstatenPage() {
  const defaults = getCurrentIsoWeekYear();
  const [weekNumber, setWeekNumber] = useState(String(defaults.weekNumber));
  const [year, setYear] = useState(String(defaults.year));
  const [scopeType, setScopeType] = useState("client");
  const [selectedKlantId, setSelectedKlantId] = useState("");
  const [selectedProjectId, setSelectedProjectId] = useState("");
  const [selectedWorkorderId, setSelectedWorkorderId] = useState("");
  const [mandagstaten, setMandagstaten] = useState([]);
  const [clients, setClients] = useState([]);
  const [projects, setProjects] = useState([]);
  const [werkbonnen, setWerkbonnen] = useState([]);
  const [viewMode, setViewMode] = useState("list");
  const [filterKlantId, setFilterKlantId] = useState("");
  const [filterProjectId, setFilterProjectId] = useState("");
  const [filterWorkorderId, setFilterWorkorderId] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterSigningState, setFilterSigningState] = useState("all");
  const [onlyOpenSignings, setOnlyOpenSignings] = useState(false);
  const [onlyClientsWithMandagstaat, setOnlyClientsWithMandagstaat] = useState(false);
  const [expandedClients, setExpandedClients] = useState({});
  const [expandedRowId, setExpandedRowId] = useState("");
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [busyKey, setBusyKey] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  useEffect(() => {
    loadDefaults();
  }, []);

  async function loadDefaults() {
    try {
      const res = await api.get("/mandagstaten/defaults/current-week");
      if (res?.data?.weekNumber) setWeekNumber(String(res.data.weekNumber));
      if (res?.data?.year) setYear(String(res.data.year));
    } catch {
      // local fallback is already present
    }
  }

  const loadData = useCallback(async () => {
    const week = Number(weekNumber);
    const isoYear = Number(year);
    if (!Number.isInteger(week) || week < 1 || week > 53) return;
    if (!Number.isInteger(isoYear) || isoYear < 2000 || isoYear > 2100) return;

    try {
      setLoading(true);
      setError("");
      const [mandagRes, clientRes, werkbonRes, projectRes] = await Promise.allSettled([
        api.get("/mandagstaten", { params: { weekNumber: week, year: isoYear } }),
        api.get("/clients"),
        api.get("/werkbonnen"),
        api.get("/projects"),
      ]);

      const list = Array.isArray(mandagRes?.value?.data?.mandagstaten)
        ? mandagRes.value.data.mandagstaten
        : [];
      const clientList = Array.isArray(clientRes?.value?.data) ? clientRes.value.data : [];
      const wbList = Array.isArray(werkbonRes?.value?.data) ? werkbonRes.value.data : [];
      const projectListFromApi = Array.isArray(projectRes?.value?.data)
        ? projectRes.value.data
        : [];
      const projectList =
        projectRes.status === "fulfilled" && projectListFromApi.length > 0
          ? projectListFromApi
          : deriveProjectsFromWerkbonnen(wbList);

      if (mandagRes.status === "rejected") throw mandagRes.reason;
      if (clientRes.status === "rejected") throw clientRes.reason;
      if (werkbonRes.status === "rejected") throw werkbonRes.reason;

      setMandagstaten(list);
      setClients(clientList);
      setProjects(projectList);
      setWerkbonnen(wbList);

      setSelectedKlantId((prev) => {
        if (prev && clientList.some((client) => String(client._id) === String(prev))) return prev;
        return clientList[0]?._id ? String(clientList[0]._id) : "";
      });
      setSelectedProjectId((prev) => {
        if (prev && projectList.some((project) => String(project._id) === String(prev))) return prev;
        return projectList[0]?._id ? String(projectList[0]._id) : "";
      });
      setSelectedWorkorderId((prev) => {
        if (prev && wbList.some((werkbon) => String(werkbon._id) === String(prev))) return prev;
        return wbList[0]?._id ? String(wbList[0]._id) : "";
      });
    } catch (err) {
      setError(err?.response?.data?.message || "Mandagstaten laden mislukt");
    } finally {
      setLoading(false);
    }
  }, [weekNumber, year]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const clientsById = useMemo(
    () => new Map((clients || []).map((client) => [String(client._id), client])),
    [clients]
  );
  const projectsById = useMemo(
    () => new Map((projects || []).map((project) => [String(project._id), project])),
    [projects]
  );
  const werkbonnenById = useMemo(
    () => new Map((werkbonnen || []).map((werkbon) => [String(werkbon._id), werkbon])),
    [werkbonnen]
  );

  const enrichedRows = useMemo(
    () =>
      (mandagstaten || []).map((row) => {
        const klantId = getKlantId(row);
        const projectId = getProjectId(row);
        const workorderId = getWorkorderId(row);
        const signingState = getSigningState(row);
        return {
          ...row,
          klantIdResolved: klantId,
          klantNaamResolved:
            row?.klantNaamSnapshot ||
            clientsById.get(klantId)?.naam ||
            "-",
          projectIdResolved: projectId,
          workorderIdResolved: workorderId,
          scopeDisplayName: getScopeDisplayName(row, { projectsById, werkbonnenById }),
          statusLabel: formatStatusLabel(row?.status),
          signingState,
          signingStateLabel: formatSigningStateLabel(signingState),
          periodLabel: `${formatDate(row?.periodStart)} t/m ${formatDate(row?.periodEnd)}`,
          lastSignatureActionLabel: formatSigningActivity(row),
          canIssuePublicSigningLink:
            String(row?.status || "").trim().toLowerCase() === "generated" &&
            Boolean(row?.publicSignatureSignerEmailPresent),
          canMarkInvoiced: String(row?.status || "").trim().toLowerCase() === "signed",
          canManualSign: String(row?.status || "").trim().toLowerCase() === "generated",
        };
      }),
    [clientsById, mandagstaten, projectsById, werkbonnenById]
  );

  const clientsWithMandagstaatOptions = useMemo(() => {
    const map = new Map();
    for (const row of enrichedRows) {
      if (!row.klantIdResolved) continue;
      if (map.has(row.klantIdResolved)) continue;
      map.set(row.klantIdResolved, {
        _id: row.klantIdResolved,
        naam: row.klantNaamResolved,
      });
    }
    return [...map.values()].sort((a, b) => String(a.naam || "").localeCompare(String(b.naam || ""), "nl-NL"));
  }, [enrichedRows]);

  const projectFilterOptions = useMemo(() => {
    const map = new Map();
    for (const row of enrichedRows) {
      if (!row.projectIdResolved) continue;
      if (map.has(row.projectIdResolved)) continue;
      map.set(row.projectIdResolved, {
        _id: row.projectIdResolved,
        naam: getProjectLabel(projectsById.get(row.projectIdResolved)) || row.scopeDisplayName,
      });
    }
    return [...map.values()].sort((a, b) => String(a.naam || "").localeCompare(String(b.naam || ""), "nl-NL"));
  }, [enrichedRows, projectsById]);

  const workorderFilterOptions = useMemo(() => {
    const map = new Map();
    for (const row of enrichedRows) {
      if (!row.workorderIdResolved) continue;
      if (map.has(row.workorderIdResolved)) continue;
      map.set(row.workorderIdResolved, {
        _id: row.workorderIdResolved,
        naam: getWorkorderLabel(werkbonnenById.get(row.workorderIdResolved)) || row.scopeDisplayName,
      });
    }
    return [...map.values()].sort((a, b) => String(a.naam || "").localeCompare(String(b.naam || ""), "nl-NL"));
  }, [enrichedRows, werkbonnenById]);

  const clientFilterOptions = useMemo(
    () => (onlyClientsWithMandagstaat ? clientsWithMandagstaatOptions : clients),
    [clients, clientsWithMandagstaatOptions, onlyClientsWithMandagstaat]
  );

  useEffect(() => {
    if (filterKlantId && !clientFilterOptions.some((client) => String(client._id) === String(filterKlantId))) {
      setFilterKlantId("");
    }
  }, [clientFilterOptions, filterKlantId]);

  async function generateMandagstaat() {
    const week = Number(weekNumber);
    const isoYear = Number(year);
    if (!Number.isInteger(week) || week < 1 || week > 53) {
      setError("Weeknummer moet tussen 1 en 53 liggen");
      return;
    }
    if (!Number.isInteger(isoYear) || isoYear < 2000 || isoYear > 2100) {
      setError("Jaar is ongeldig");
      return;
    }

    try {
      setGenerating(true);
      setError("");
      setSuccess("");
      let payload = {
        weekNumber: week,
        year: isoYear,
      };

      if (scopeType === "project") {
        if (!selectedProjectId) {
          setError("Selecteer eerst een project");
          return;
        }
        payload = { ...payload, projectId: selectedProjectId };
      } else if (scopeType === "workorder") {
        if (!selectedWorkorderId) {
          setError("Selecteer eerst een werkbon");
          return;
        }
        payload = { ...payload, workorderId: selectedWorkorderId };
      } else {
        if (!selectedKlantId) {
          setError("Selecteer eerst een klant");
          return;
        }
        payload = { ...payload, klantId: selectedKlantId };
      }

      await api.post("/mandagstaten/generate", payload);
      setSuccess("Mandagstaat gegenereerd.");
      await loadData();
    } catch (err) {
      setError(err?.response?.data?.message || "Mandagstaat genereren mislukt");
    } finally {
      setGenerating(false);
    }
  }

  async function downloadPdf(row) {
    try {
      const key = `${row._id}:pdf`;
      setBusyKey(key);
      setError("");
      const res = await api.get(`/mandagstaten/${row._id}/pdf`, {
        responseType: "blob",
      });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${row.documentNumber || `mandagstaat-${row._id}`}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(err?.response?.data?.message || "PDF downloaden mislukt");
    } finally {
      setBusyKey("");
    }
  }

  async function signMandagstaat(row) {
    const signedByName = window.prompt("Naam ondertekenaar", row.signedByName || "");
    if (signedByName === null) return;
    const signedFileUrl = window.prompt(
      "URL getekend document (optioneel)",
      row.signedFileUrl || ""
    );
    if (signedFileUrl === null) return;

    try {
      const key = `${row._id}:sign`;
      setBusyKey(key);
      setError("");
      setSuccess("");
      await api.post(`/mandagstaten/${row._id}/sign`, {
        signedByName,
        signedFileUrl,
      });
      setSuccess("Mandagstaat op ondertekend gezet.");
      await loadData();
    } catch (err) {
      setError(err?.response?.data?.message || "Ondertekenen mislukt");
    } finally {
      setBusyKey("");
    }
  }

  async function markAsInvoiced(row) {
    const invoiceId = window.prompt("Factuur-ID (optioneel)", row.invoiceId || "");
    if (invoiceId === null) return;

    try {
      const key = `${row._id}:invoice`;
      setBusyKey(key);
      setError("");
      setSuccess("");
      await api.post(`/mandagstaten/${row._id}/invoice`, {
        invoiceId: String(invoiceId || "").trim(),
      });
      setSuccess("Mandagstaat op gefactureerd gezet.");
      await loadData();
    } catch (err) {
      setError(err?.response?.data?.message || "Status naar gefactureerd zetten mislukt");
    } finally {
      setBusyKey("");
    }
  }

  async function issuePublicSigningLink(row) {
    try {
      const key = `${row._id}:public-sign`;
      setBusyKey(key);
      setError("");
      setSuccess("");
      const res = await api.post(`/mandagstaten/${row._id}/public-sign-request`);
      const signUrl = String(res?.data?.publicSignatureRequest?.signUrl || "").trim();
      let nextMessage = res?.data?.message || "Publieke ondertekenlink aangemaakt.";

      if (signUrl && navigator.clipboard?.writeText) {
        try {
          await navigator.clipboard.writeText(signUrl);
          nextMessage += " Link gekopieerd.";
        } catch {
          // no-op
        }
      }
      if (signUrl) {
        window.open(signUrl, "_blank", "noopener,noreferrer");
        nextMessage += " Link geopend.";
      }
      setSuccess(nextMessage.trim());
      await loadData();
    } catch (err) {
      setError(err?.response?.data?.message || "Publieke ondertekenlink aanmaken mislukt");
    } finally {
      setBusyKey("");
    }
  }

  const missingWarnings = useMemo(() => {
    if (scopeType !== "client") return [];
    const week = Number(weekNumber);
    const isoYear = Number(year);
    if (!Number.isInteger(week) || !Number.isInteger(isoYear)) return [];
    const { start, endExclusive } = getIsoWeekRange(isoYear, week);

    const klantenMetUren = new Set();
    for (const wb of werkbonnen || []) {
      const klantId = wb?.clientId?._id || wb?.clientId;
      if (!klantId) continue;
      if (hasHoursInWeek(wb, start, endExclusive)) {
        klantenMetUren.add(String(klantId));
      }
    }

    const klantenMetMandagstaat = new Set(enrichedRows.map((row) => row.klantIdResolved));

    return (clients || [])
      .filter((client) => {
        const id = String(client._id);
        return klantenMetUren.has(id) && !klantenMetMandagstaat.has(id);
      })
      .map((client) => ({
        klantId: String(client._id),
        naam: client.naam || "-",
      }));
  }, [clients, enrichedRows, scopeType, werkbonnen, weekNumber, year]);

  const scopeOptions = useMemo(
    () => [
      { value: "client", label: "Klant" },
      { value: "project", label: "Project" },
      { value: "workorder", label: "Werkbon" },
    ],
    []
  );

  const statusFilterOptions = useMemo(
    () => [
      { value: "all", label: "Alle statussen" },
      { value: "draft", label: "Concept" },
      { value: "generated", label: "Generated" },
      { value: "signed", label: "Ondertekend" },
      { value: "invoiced", label: "Gefactureerd" },
    ],
    []
  );

  const signingFilterOptions = useMemo(
    () => [
      { value: "all", label: "Alle ondertekenstatussen" },
      { value: "not_sent", label: "Niet verzonden" },
      { value: "pending", label: "Openstaand" },
      { value: "signed", label: "Publiek ondertekend" },
      { value: "rejected", label: "Afgewezen" },
      { value: "manual_signed", label: "Handmatig ondertekend" },
    ],
    []
  );

  const selectedScopeLabel = useMemo(() => {
    if (scopeType === "project") {
      const project = projects.find((item) => String(item._id) === String(selectedProjectId));
      return getProjectLabel(project);
    }
    if (scopeType === "workorder") {
      const werkbon = werkbonnen.find((item) => String(item._id) === String(selectedWorkorderId));
      return getWorkorderLabel(werkbon);
    }
    const client = clients.find((item) => String(item._id) === String(selectedKlantId));
    return client?.naam || "";
  }, [clients, projects, scopeType, selectedKlantId, selectedProjectId, selectedWorkorderId, werkbonnen]);

  const filteredRows = useMemo(
    () =>
      enrichedRows.filter((row) => {
        if (filterKlantId && row.klantIdResolved !== String(filterKlantId)) return false;
        if (filterProjectId && row.projectIdResolved !== String(filterProjectId)) return false;
        if (filterWorkorderId && row.workorderIdResolved !== String(filterWorkorderId)) return false;
        if (filterStatus !== "all" && String(row.status || "").trim().toLowerCase() !== filterStatus) {
          return false;
        }
        if (filterSigningState !== "all" && row.signingState !== filterSigningState) return false;
        if (onlyOpenSignings && row.signingState !== "pending") return false;
        return true;
      }),
    [
      enrichedRows,
      filterKlantId,
      filterProjectId,
      filterSigningState,
      filterStatus,
      filterWorkorderId,
      onlyOpenSignings,
    ]
  );

  const stats = useMemo(() => {
    const next = {
      total: filteredRows.length,
      generated: 0,
      pending: 0,
      signed: 0,
      rejected: 0,
      invoiced: 0,
    };

    for (const row of filteredRows) {
      const status = String(row.status || "").trim().toLowerCase();
      if (status === "draft" || status === "generated") next.generated += 1;
      if (row.signingState === "pending") next.pending += 1;
      if (status === "signed") next.signed += 1;
      if (row.signingState === "rejected") next.rejected += 1;
      if (status === "invoiced") next.invoiced += 1;
    }

    return next;
  }, [filteredRows]);

  const groupedByClient = useMemo(() => {
    const map = new Map();
    for (const row of filteredRows) {
      const key = row.klantIdResolved || "__unknown__";
      if (!map.has(key)) {
        map.set(key, {
          clientId: key,
          clientName: row.klantNaamResolved || "Onbekend",
          count: 0,
          totalHours: 0,
          signedCount: 0,
          openCount: 0,
          rows: [],
        });
      }
      const bucket = map.get(key);
      bucket.count += 1;
      bucket.totalHours += Number(row.totalHoursWeek || 0);
      if (String(row.status || "").trim().toLowerCase() === "signed") bucket.signedCount += 1;
      if (row.signingState === "pending") bucket.openCount += 1;
      bucket.rows.push(row);
    }
    return [...map.values()].sort((a, b) => String(a.clientName || "").localeCompare(String(b.clientName || ""), "nl-NL"));
  }, [filteredRows]);

  function toggleClientGroup(clientId) {
    setExpandedClients((prev) => ({
      ...prev,
      [clientId]: !prev[clientId],
    }));
  }

  function clearFilters() {
    setFilterKlantId("");
    setFilterProjectId("");
    setFilterWorkorderId("");
    setFilterStatus("all");
    setFilterSigningState("all");
    setOnlyOpenSignings(false);
  }

  function renderActionButtons(row) {
    return (
      <div className="flex flex-wrap justify-end gap-2">
        <button
          type="button"
          onClick={() => setExpandedRowId((prev) => (prev === row._id ? "" : row._id))}
          className="rounded border px-2 py-1 text-xs hover:bg-gray-50"
        >
          {expandedRowId === row._id ? "Sluit" : "Bekijk"}
        </button>
        <button
          type="button"
          onClick={() => downloadPdf(row)}
          disabled={busyKey === `${row._id}:pdf`}
          className="rounded border px-2 py-1 text-xs hover:bg-gray-50 disabled:opacity-60"
        >
          PDF
        </button>
        <button
          type="button"
          onClick={() => issuePublicSigningLink(row)}
          disabled={
            busyKey === `${row._id}:public-sign` || !row.canIssuePublicSigningLink
          }
          className="rounded border border-amber-300 bg-amber-50 px-2 py-1 text-xs text-amber-800 hover:bg-amber-100 disabled:opacity-50"
          title={
            row.canIssuePublicSigningLink
              ? "Maak of vernieuw een publieke ondertekenlink"
              : "Geen publieke ondertekenlink mogelijk voor deze mandagstaat"
          }
        >
          Ondertekenlink
        </button>
        <button
          type="button"
          onClick={() => signMandagstaat(row)}
          disabled={busyKey === `${row._id}:sign` || !row.canManualSign}
          className="rounded bg-indigo-600 px-2 py-1 text-xs text-white hover:bg-indigo-700 disabled:opacity-60"
        >
          Onderteken
        </button>
        <button
          type="button"
          onClick={() => markAsInvoiced(row)}
          disabled={busyKey === `${row._id}:invoice` || !row.canMarkInvoiced}
          className="rounded bg-emerald-600 px-2 py-1 text-xs text-white hover:bg-emerald-700 disabled:opacity-60"
        >
          Gefactureerd
        </button>
      </div>
    );
  }

  function renderExpandedRow(row) {
    return (
      <tr key={`${row._id}:details`} className="border-b bg-slate-50">
        <td colSpan={9} className="px-4 py-3">
          <div className="grid gap-3 md:grid-cols-3">
            <div className="rounded-xl border border-slate-200 bg-white px-4 py-3">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Document</p>
              <p className="mt-1 text-sm text-slate-900">{row.documentNumber || "-"}</p>
              <p className="mt-2 text-xs text-slate-500">Periode</p>
              <p className="text-sm text-slate-900">{row.periodLabel}</p>
            </div>
            <div className="rounded-xl border border-slate-200 bg-white px-4 py-3">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Ondertekening</p>
              <p className="mt-1 text-sm text-slate-900">{row.signingStateLabel}</p>
              <p className="mt-2 text-xs text-slate-500">Laatste actie</p>
              <p className="text-sm text-slate-900">{row.lastSignatureActionLabel}</p>
            </div>
            <div className="rounded-xl border border-slate-200 bg-white px-4 py-3">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Overig</p>
              <p className="mt-1 text-sm text-slate-900">
                Ondertekend door: {row.signedByName || "-"}
              </p>
              <p className="mt-2 text-sm text-slate-900">
                Gefactureerd: {row.invoiceId ? "Ja" : "Nee"}
              </p>
            </div>
          </div>
        </td>
      </tr>
    );
  }

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-2xl font-bold">Mandagstaten</h2>
        <p className="text-sm text-gray-600">
          Overzicht, filters, publieke ondertekening en klantgroepering voor mandagstaten.
        </p>
      </div>

      <section className="grid gap-3 md:grid-cols-3 xl:grid-cols-6">
        <StatCard label="Totaal" value={stats.total} />
        <StatCard label="Generated / concept" value={stats.generated} />
        <StatCard label="Pending signing" value={stats.pending} tone="pending" />
        <StatCard label="Signed" value={stats.signed} tone="signed" />
        <StatCard label="Rejected" value={stats.rejected} tone="rejected" />
        <StatCard label="Invoiced" value={stats.invoiced} tone="invoiced" />
      </section>

      <section className="rounded-2xl bg-white p-4 shadow space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h3 className="text-lg font-semibold">Genereren</h3>
            <p className="text-sm text-gray-500">
              Nieuwe mandagstaat aanmaken voor klant, project of werkbon.
            </p>
          </div>
          <div className="inline-flex rounded-xl border border-slate-200 bg-slate-50 p-1">
            <button
              type="button"
              onClick={() => setViewMode("list")}
              className={`rounded-lg px-3 py-1.5 text-sm ${
                viewMode === "list" ? "bg-white shadow text-slate-900" : "text-slate-600"
              }`}
            >
              Lijst
            </button>
            <button
              type="button"
              onClick={() => setViewMode("customer")}
              className={`rounded-lg px-3 py-1.5 text-sm ${
                viewMode === "customer" ? "bg-white shadow text-slate-900" : "text-slate-600"
              }`}
            >
              Per klant
            </button>
          </div>
        </div>

        <div className="grid gap-3 md:grid-cols-5">
          <label className="text-sm">
            <span className="mb-1 block text-gray-600">Scope</span>
            <select
              value={scopeType}
              onChange={(e) => setScopeType(e.target.value)}
              className="w-full rounded border p-2"
            >
              {scopeOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>

          <label className="text-sm">
            <span className="mb-1 block text-gray-600">Week</span>
            <input
              type="number"
              min="1"
              max="53"
              value={weekNumber}
              onChange={(e) => setWeekNumber(e.target.value)}
              className="w-full rounded border p-2"
            />
          </label>

          <label className="text-sm">
            <span className="mb-1 block text-gray-600">Jaar</span>
            <input
              type="number"
              min="2000"
              max="2100"
              value={year}
              onChange={(e) => setYear(e.target.value)}
              className="w-full rounded border p-2"
            />
          </label>

          {scopeType === "project" ? (
            <label className="text-sm md:col-span-2">
              <span className="mb-1 block text-gray-600">Project voor generatie</span>
              <select
                value={selectedProjectId}
                onChange={(e) => setSelectedProjectId(e.target.value)}
                className="w-full rounded border p-2"
              >
                <option value="">Kies project</option>
                {projects.map((project) => (
                  <option key={project._id} value={project._id}>
                    {getProjectLabel(project)}
                  </option>
                ))}
              </select>
            </label>
          ) : scopeType === "workorder" ? (
            <label className="text-sm md:col-span-2">
              <span className="mb-1 block text-gray-600">Werkbon voor generatie</span>
              <select
                value={selectedWorkorderId}
                onChange={(e) => setSelectedWorkorderId(e.target.value)}
                className="w-full rounded border p-2"
              >
                <option value="">Kies werkbon</option>
                {werkbonnen.map((werkbon) => (
                  <option key={werkbon._id} value={werkbon._id}>
                    {getWorkorderLabel(werkbon)}
                  </option>
                ))}
              </select>
            </label>
          ) : (
            <label className="text-sm md:col-span-2">
              <span className="mb-1 block text-gray-600">Klant voor generatie</span>
              <select
                value={selectedKlantId}
                onChange={(e) => setSelectedKlantId(e.target.value)}
                className="w-full rounded border p-2"
              >
                <option value="">Kies klant</option>
                {clients.map((client) => (
                  <option key={client._id} value={client._id}>
                    {client.naam}
                  </option>
                ))}
              </select>
            </label>
          )}

          <div className="flex items-end gap-2">
            <button
              type="button"
              onClick={generateMandagstaat}
              disabled={generating}
              className="rounded bg-blue-600 px-4 py-2 text-sm text-white hover:bg-blue-700 disabled:opacity-60"
            >
              {generating ? "Genereren..." : "Genereer"}
            </button>
            <button
              type="button"
              onClick={loadData}
              className="rounded border px-4 py-2 text-sm hover:bg-gray-50"
            >
              Vernieuwen
            </button>
          </div>
        </div>

        {selectedScopeLabel ? (
          <p className="text-xs text-gray-500">
            Geselecteerde scope: <strong>{formatScopeType(scopeType)}</strong> - {selectedScopeLabel}
          </p>
        ) : null}
      </section>

      <section className="rounded-2xl bg-white p-4 shadow space-y-4">
        <div>
          <h3 className="text-lg font-semibold">Filters</h3>
          <p className="text-sm text-gray-500">
            Filter op klant, scope en ondertekenstatus. Statistieken en views volgen deze selectie.
          </p>
        </div>

        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
          <label className="text-sm">
            <span className="mb-1 block text-gray-600">Klant</span>
            <select
              value={filterKlantId}
              onChange={(e) => setFilterKlantId(e.target.value)}
              className="w-full rounded border p-2"
            >
              <option value="">Alle klanten</option>
              {clientFilterOptions.map((client) => (
                <option key={client._id} value={client._id}>
                  {client.naam}
                </option>
              ))}
            </select>
          </label>

          <label className="text-sm">
            <span className="mb-1 block text-gray-600">Project</span>
            <select
              value={filterProjectId}
              onChange={(e) => setFilterProjectId(e.target.value)}
              className="w-full rounded border p-2"
            >
              <option value="">Alle projecten</option>
              {projectFilterOptions.map((project) => (
                <option key={project._id} value={project._id}>
                  {project.naam}
                </option>
              ))}
            </select>
          </label>

          <label className="text-sm">
            <span className="mb-1 block text-gray-600">Werkbon</span>
            <select
              value={filterWorkorderId}
              onChange={(e) => setFilterWorkorderId(e.target.value)}
              className="w-full rounded border p-2"
            >
              <option value="">Alle werkbonnen</option>
              {workorderFilterOptions.map((werkbon) => (
                <option key={werkbon._id} value={werkbon._id}>
                  {werkbon.naam}
                </option>
              ))}
            </select>
          </label>

          <label className="text-sm">
            <span className="mb-1 block text-gray-600">Status</span>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="w-full rounded border p-2"
            >
              {statusFilterOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>

          <label className="text-sm">
            <span className="mb-1 block text-gray-600">Ondertekenstatus</span>
            <select
              value={filterSigningState}
              onChange={(e) => setFilterSigningState(e.target.value)}
              className="w-full rounded border p-2"
            >
              {signingFilterOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>

          <label className="flex items-end gap-2 rounded border px-3 py-2 text-sm">
            <input
              type="checkbox"
              checked={onlyOpenSignings}
              onChange={(e) => setOnlyOpenSignings(e.target.checked)}
              className="h-4 w-4 rounded border-slate-300"
            />
            <span>Alleen openstaande ondertekeningen</span>
          </label>

          <label className="flex items-end gap-2 rounded border px-3 py-2 text-sm">
            <input
              type="checkbox"
              checked={onlyClientsWithMandagstaat}
              onChange={(e) => setOnlyClientsWithMandagstaat(e.target.checked)}
              className="h-4 w-4 rounded border-slate-300"
            />
            <span>Alleen klanten met mandagstaat</span>
          </label>

          <div className="flex items-end">
            <button
              type="button"
              onClick={clearFilters}
              className="rounded border px-4 py-2 text-sm hover:bg-gray-50"
            >
              Filters wissen
            </button>
          </div>
        </div>

        {error && <p className="text-sm text-red-600">{error}</p>}
        {success && <p className="text-sm text-green-700">{success}</p>}
      </section>

      {missingWarnings.length > 0 && (
        <section className="rounded-xl border border-amber-200 bg-amber-50 p-4">
          <h3 className="font-semibold text-amber-800">
            Waarschuwing: nog geen mandagstaat gegenereerd
          </h3>
          <ul className="mt-2 list-disc pl-5 text-sm text-amber-900">
            {missingWarnings.map((item) => (
              <li key={item.klantId}>
                Voor klant <strong>{item.naam}</strong> is week {weekNumber}/{year} nog niet gegenereerd.
              </li>
            ))}
          </ul>
        </section>
      )}

      <section className="rounded-2xl bg-white p-4 shadow">
        {loading && <p className="text-sm text-gray-500">Laden...</p>}

        {!loading && filteredRows.length === 0 && (
          <p className="text-sm text-gray-500">Geen mandagstaten gevonden voor deze selectie.</p>
        )}

        {!loading && filteredRows.length > 0 && viewMode === "list" && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left">
                  <th className="py-2 pr-3">Document</th>
                  <th className="py-2 pr-3">Klant</th>
                  <th className="py-2 pr-3">Scope</th>
                  <th className="py-2 pr-3">Week / Jaar</th>
                  <th className="py-2 pr-3">Periode</th>
                  <th className="py-2 pr-3">Totaal uren</th>
                  <th className="py-2 pr-3">Status</th>
                  <th className="py-2 pr-3">Ondertekenstatus</th>
                  <th className="py-2 pr-3">Laatste actie</th>
                  <th className="py-2 text-right">Acties</th>
                </tr>
              </thead>
              <tbody>
                {filteredRows.map((row) => (
                  <Fragment key={row._id}>
                    <tr key={row._id} className="border-b align-top">
                      <td className="py-2 pr-3">
                        <div className="font-medium">{row.documentNumber || "-"}</div>
                        <div className="text-xs text-gray-500">{row._id}</div>
                      </td>
                      <td className="py-2 pr-3">{row.klantNaamResolved}</td>
                      <td className="py-2 pr-3">
                        <div className="font-medium">{formatScopeType(row.scopeType)}</div>
                        <div className="text-xs text-gray-500">{row.scopeDisplayName}</div>
                      </td>
                      <td className="py-2 pr-3">
                        {row.weekNumber}/{row.year}
                      </td>
                      <td className="py-2 pr-3">{row.periodLabel}</td>
                      <td className="py-2 pr-3">{formatHours(row.totalHoursWeek)}</td>
                      <td className="py-2 pr-3">
                        <InfoBadge
                          label={row.statusLabel}
                          tone={String(row.status || "").trim().toLowerCase()}
                        />
                      </td>
                      <td className="py-2 pr-3">
                        <InfoBadge
                          label={row.signingStateLabel}
                          tone={row.signingState === "pending" ? "pending" : row.signingState}
                        />
                      </td>
                      <td className="py-2 pr-3">{row.lastSignatureActionLabel}</td>
                      <td className="py-2 text-right">{renderActionButtons(row)}</td>
                    </tr>
                    {expandedRowId === row._id ? renderExpandedRow(row) : null}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {!loading && filteredRows.length > 0 && viewMode === "customer" && (
          <div className="space-y-3">
            {groupedByClient.map((group) => (
              <div key={group.clientId} className="rounded-2xl border border-slate-200">
                <button
                  type="button"
                  onClick={() => toggleClientGroup(group.clientId)}
                  className="flex w-full flex-wrap items-center justify-between gap-3 px-4 py-4 text-left"
                >
                  <div>
                    <p className="text-base font-semibold text-slate-900">{group.clientName}</p>
                    <p className="text-sm text-slate-500">{group.count} mandagstaat(en)</p>
                  </div>
                  <div className="flex flex-wrap gap-2 text-xs">
                    <InfoBadge label={`${formatHours(group.totalHours)} uur`} />
                    <InfoBadge label={`${group.signedCount} ondertekend`} tone="signed" />
                    <InfoBadge label={`${group.openCount} openstaand`} tone="pending" />
                  </div>
                </button>

                {expandedClients[group.clientId] ? (
                  <div className="border-t border-slate-200 px-4 py-4">
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b text-left">
                            <th className="py-2 pr-3">Document</th>
                            <th className="py-2 pr-3">Scope</th>
                            <th className="py-2 pr-3">Week / Jaar</th>
                            <th className="py-2 pr-3">Uren</th>
                            <th className="py-2 pr-3">Status</th>
                            <th className="py-2 pr-3">Ondertekenstatus</th>
                            <th className="py-2 pr-3">Laatste actie</th>
                            <th className="py-2 text-right">Acties</th>
                          </tr>
                        </thead>
                        <tbody>
                          {group.rows.map((row) => (
                            <tr key={`${group.clientId}:${row._id}`} className="border-b align-top last:border-b-0">
                              <td className="py-2 pr-3">
                                <div className="font-medium">{row.documentNumber || "-"}</div>
                                <div className="text-xs text-gray-500">{row.periodLabel}</div>
                              </td>
                              <td className="py-2 pr-3">
                                <div className="font-medium">{formatScopeType(row.scopeType)}</div>
                                <div className="text-xs text-gray-500">{row.scopeDisplayName}</div>
                              </td>
                              <td className="py-2 pr-3">
                                {row.weekNumber}/{row.year}
                              </td>
                              <td className="py-2 pr-3">{formatHours(row.totalHoursWeek)}</td>
                              <td className="py-2 pr-3">
                                <InfoBadge
                                  label={row.statusLabel}
                                  tone={String(row.status || "").trim().toLowerCase()}
                                />
                              </td>
                              <td className="py-2 pr-3">
                                <InfoBadge
                                  label={row.signingStateLabel}
                                  tone={row.signingState === "pending" ? "pending" : row.signingState}
                                />
                              </td>
                              <td className="py-2 pr-3">{row.lastSignatureActionLabel}</td>
                              <td className="py-2 text-right">{renderActionButtons(row)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ) : null}
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
